import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "../../components/Button";
import { Checkbox } from "../../components/Checkbox";
import { ComboboxPopover } from "../../components/ComboboxPopover";
import { ConfirmModal } from "../../components/ConfirmModal";
import { SelectPopover } from "../../components/SelectPopover";
import { MultiSelectPopover } from "../../components/MultiSelectPopover";
import { Switch } from "../../components/Switch";
import { TablePagination } from "../../components/TablePagination";
import { TextInput } from "../../components/TextInput";
import { MESSAGING_ENABLED } from "../../constants/features";
import { US_STATES } from "../../constants/usStates";
import { useAuth } from "../../hooks/useAuth";
import { useDebounce } from "../../hooks/useDebounce";
import { downloadBlobFile } from "../../utils/file-download";
import { getSafeErrorMessage } from "../../utils/errorHandler";
import { PERMISSIONS, hasPermission } from "../../utils/permissions";
import {
  bulkDeleteTrustedContacts,
  createTrustedContact,
  deleteTrustedContact,
  getAllTrustedContacts,
  getTrustedContactOrganizations,
  getTrustedContacts,
  setTrustedContactFavorite,
  updateTrustedContact,
} from "../../services/trusted-contacts";
import {
  TRUSTED_CONTACT_TYPES,
  type ImportTrustedContactsResult,
  type TrustedContact,
  type TrustedContactInput,
  type TrustedContactType,
} from "../../types/trusted-contacts";
import type { MessagingContactSummary } from "../../types/messaging";
import { ComposeMessageModal } from "../messaging/ComposeMessageModal";
import { CONTACT_TYPE_LABELS, contactTypeFromLabel, contactTypeLabel } from "./contactTypes";
import { ImportContactsModal } from "./ImportContactsModal";
import { TrustedContactModal } from "./TrustedContactModal";
import { TrustedContactRow } from "./TrustedContactRow";
import { buildTrustedContactsCsv, trustedContactsCsvFileName } from "./trustedContactsCsv";
import "./TrustedContacts.css";

const ALL_TYPES = "All Contact Types";
const ALL_STATES = "All States";

const TYPE_OPTIONS = [ALL_TYPES, ...TRUSTED_CONTACT_TYPES.map((t) => CONTACT_TYPE_LABELS[t])];

/** The only sizes a coach can pick — kept small deliberately, and within what the API will honour. */
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;
const DEFAULT_PAGE_SIZE = 10;

/**
 * Own key, not shared with the players list's per-organization `playersPageSize_<id>` scheme —
 * this preference is not organization-scoped, so a shared key would let switching features stomp
 * on each other's stored size.
 */
const PAGE_SIZE_STORAGE_KEY = "trustedContactsPageSize";

const isPageSize = (value: unknown): value is number =>
  typeof value === "number" && (PAGE_SIZE_OPTIONS as readonly number[]).includes(value);

/**
 * Falls back to the default on anything but one of the offered sizes — a hand-edited or stale
 * value (or a browser that throws reading storage at all) must not hand the table a page size the
 * selector itself does not offer.
 */
const getStoredPageSize = (): number => {
  try {
    const stored = localStorage.getItem(PAGE_SIZE_STORAGE_KEY);
    if (stored === null) return DEFAULT_PAGE_SIZE;
    const parsed = Number(stored);
    return isPageSize(parsed) ? parsed : DEFAULT_PAGE_SIZE;
  } catch {
    return DEFAULT_PAGE_SIZE;
  }
};

const savePageSize = (pageSize: number): void => {
  if (!isPageSize(pageSize)) return;
  try {
    localStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(pageSize));
  } catch {
    // A browser with site data blocked throws on access. Losing the preference is fine; crashing
    // the page over it is not.
  }
};

/**
 * A directory contact as the messaging composer's recipient list understands it.
 *
 * `contactType: "TrustedContact"` is the third member of `MessagingContactType`, and the id is the
 * `trusted_contacts` row — so the resulting thread lands in the inbox under the contact's name
 * rather than as an anonymous number.
 */
const toRecipient = (contact: TrustedContact): MessagingContactSummary => ({
  contactId: contact.id,
  contactType: "TrustedContact",
  displayName: contact.name,
  phoneNumber: contact.phoneE164,
  playerName: null,
  gradYear: null,
});

/**
 * "Ryan Johnson and Steve Manning", "Ryan, Steve and Kevin".
 *
 * Every name, never "and 3 others": this list is the answer to "who did you leave out", and a
 * coach cannot chase down a contact they were not told about. Bounded by the page size.
 */
const nameList = (names: string[]): string =>
  names.length <= 1
    ? (names[0] ?? "")
    : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;

/** Which contact the add/edit modal is open on, or null when it is closed. */
type Editor = { contact: TrustedContact | null };

/**
 * The organization's shared directory of coaches, scouts and other contacts.
 *
 * Filtering, paging and search all run server-side: the directory is org-wide and shared, so a
 * client-side filter over one page would silently disagree with the count beside it. Selection is
 * therefore also per page — see the note on `selected`.
 */
export const TrustedContactsSection = () => {
  const { user } = useAuth();

  const [contacts, setContacts] = useState<TrustedContact[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  /** Read once on mount and validated against the offered sizes — see `getStoredPageSize`. */
  const [pageSize, setPageSize] = useState(() => getStoredPageSize());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);
  const [typeFilter, setTypeFilter] = useState<TrustedContactType | null>(null);
  /*
    Several at once: a coach recruiting a region wants OH and IN and KY together, not one at a
    time. Empty means no narrowing.
  */
  const [stateFilters, setStateFilters] = useState<string[]>([]);
  const [favoritesOnly, setFavoritesOnly] = useState(false);

  /*
    Doubles as the organization combobox's own typed text and the table's applied filter — there is
    no separate "committed" value, the same as the State field elsewhere ComboboxPopover feeds. It
    is debounced below both for the suggestion fetch and for the table query, the same hook and the
    same 300ms the main search box already uses.
  */
  const [organizationFilter, setOrganizationFilter] = useState<string | null>(null);
  const debouncedOrganizationFilter = useDebounce(organizationFilter, 300);
  /** Everything the server will offer without a search term — what the arrow browses. */
  const [organizationBrowseList, setOrganizationBrowseList] = useState<string[]>([]);
  /** Extra names found by searching past the cap. Merged with the browse list, never replacing it. */
  const [organizationMatches, setOrganizationMatches] = useState<string[]>([]);
  /** True when the server capped the suggestion list before every match had been returned. */
  const [organizationsTruncated, setOrganizationsTruncated] = useState(false);

  /**
   * Scoped to the one contact whose star is mid-flight, not a single shared flag — starring
   * several rows in quick succession must not grey out a row that is not actually waiting on
   * anything.
   */
  const [pendingFavoriteIds, setPendingFavoriteIds] = useState<Set<string>>(new Set());

  /*
    Selection is scoped to the page on screen and cleared whenever the page or the filters change.
    Carrying it across pages would let a coach send to people they can no longer see, and the
    "select all" box would have to mean something different from the rows under it. It is also
    pruned against every reload — see `applyPage`.
  */
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const [editor, setEditor] = useState<Editor | null>(null);
  const [deleting, setDeleting] = useState<TrustedContact | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  /*
    The contacts the "remove these N" confirmation is open on, snapshotted when it opens, or null
    when it is closed. Not derived from `selected`: a reload landing under the open dialog prunes
    the selection, and a dialog reading it live re-titles itself from "Remove 2 contacts" to
    "Remove 0 contacts" under the coach's cursor, then confirms to nothing.
  */
  const [bulkRemoving, setBulkRemoving] = useState<TrustedContact[] | null>(null);
  /*
    Its own flag rather than sharing `deleteBusy`. ConfirmModal deliberately keeps Cancel and
    Escape live while busy, so a coach can dismiss a slow single delete and open this one — and a
    shared flag would greet them with a Remove button already disabled for no visible reason.
  */
  const [bulkBusy, setBulkBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const [composeRecipients, setComposeRecipients] = useState<MessagingContactSummary[] | null>(null);

  /*
    One controller for every fetch this component makes, aborted before each new one and on
    unmount. A write-then-reload used to race the effect's in-flight request: the older response
    could land last and put a just-deleted contact back on screen.
  */
  const fetchRef = useRef<AbortController | null>(null);
  const exportRef = useRef<AbortController | null>(null);
  const organizationsFetchRef = useRef<AbortController | null>(null);
  const tableRef = useRef<HTMLTableElement | null>(null);

  const messagingAvailable =
    MESSAGING_ENABLED &&
    user?.profile?.messagingEnabled !== false &&
    hasPermission(user?.profile, PERMISSIONS.messaging);

  /** Applies a freshly-loaded page, dropping any selected id the page no longer holds. */
  const applyPage = useCallback((items: TrustedContact[], count: number) => {
    setContacts(items);
    setTotalCount(count);
    setSelected((current) => {
      if (current.size === 0) return current;
      const next = new Set(items.filter((c) => current.has(c.id)).map((c) => c.id));
      return next.size === current.size ? current : next;
    });
  }, []);

  const load = useCallback(async () => {
    fetchRef.current?.abort();
    const controller = new AbortController();
    fetchRef.current = controller;

    setLoading(true);
    setError(null);
    try {
      const result = await getTrustedContacts(
        {
          search: debouncedSearch,
          contactType: typeFilter,
          organization: debouncedOrganizationFilter,
          states: stateFilters,
          isFavorite: favoritesOnly || undefined,
          pageNumber: page,
          pageSize,
        },
        controller.signal
      );
      if (controller.signal.aborted) return;
      applyPage(result.items, result.totalCount);
    } catch (err) {
      if (controller.signal.aborted) return;
      setError(getSafeErrorMessage(err, "Could not load contacts."));
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [
    debouncedSearch,
    typeFilter,
    debouncedOrganizationFilter,
    stateFilters,
    favoritesOnly,
    page,
    pageSize,
    applyPage,
  ]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(
    () => () => {
      fetchRef.current?.abort();
      exportRef.current?.abort();
      organizationsFetchRef.current?.abort();
    },
    []
  );

  // Narrowing the list, or resizing the page, changes which rows exist, so a selection made
  // against the old list or the old page no longer describes anything the coach can see.
  useEffect(() => {
    setSelected(new Set());
  }, [
    debouncedSearch,
    typeFilter,
    debouncedOrganizationFilter,
    stateFilters,
    favoritesOnly,
    page,
    pageSize,
  ]);

  /*
    Step back off a page that no longer exists.

    Removing the last rows of the last page — or a colleague doing it first — leaves the coach on
    an empty page under a pager reading "Showing 81 to 80 of 80". Clamping here, against the count
    a load actually came back with, rather than in each remove handler against a predicted one:
    the prediction is wrong whenever the server skipped an id the page still believed in, which is
    the exact case bulk delete's skipped-ids report exists to handle.
  */
  useEffect(() => {
    if (loading) return;
    const lastPage = Math.max(1, Math.ceil(totalCount / pageSize));
    if (page > lastPage) setPage(lastPage);
  }, [loading, totalCount, page, pageSize]);

  /*
    The suggestion list is fetched UNFILTERED, and the narrowing happens in the popover.

    It used to be fetched with the filter's own value, which made the control unusable in a way
    that is obvious in hindsight: pick "Ohio State" and the server returns the organizations
    matching "Ohio State", so the list holds exactly the one thing already in the box. Opening the
    arrow showed a menu with nothing to choose, and backspacing had to wait out a 300ms debounce
    and a round trip before the options widened again.

    The server caps and alphabetises this list, so it is small enough to hold and filter locally —
    which also makes typing and backspacing instant instead of debounced.

    `search` is still passed when the cap actually cut the list off, so typing can reach past the
    first page. Those results are merged with the browse list rather than replacing it, so opening
    the arrow always offers the same full set.
  */
  const loadOrganizations = useCallback(async (search: string) => {
    organizationsFetchRef.current?.abort();
    const controller = new AbortController();
    organizationsFetchRef.current = controller;
    try {
      const result = await getTrustedContactOrganizations(search, controller.signal);
      if (controller.signal.aborted) return;
      if (search === "") {
        setOrganizationBrowseList(result.organizations);
        setOrganizationsTruncated(result.truncated);
      } else {
        setOrganizationMatches(result.organizations);
      }
    } catch {
      if (controller.signal.aborted) return;
      // A filter that cannot list its options is a degraded filter, not a broken page.
    }
  }, []);

  // The browse list, read once on mount. The write paths below re-read it themselves, so a newly
  // typed organization shows up without this effect needing a version counter to watch.
  useEffect(() => {
    void loadOrganizations("");
  }, [loadOrganizations]);

  /*
    Only when the cap cut the list off. With the whole set already local there is nothing a server
    round trip can add, and firing one per keystroke would be work for its own sake.
  */
  useEffect(() => {
    const term = debouncedOrganizationFilter?.trim() ?? "";
    if (!organizationsTruncated || term === "") return;
    void loadOrganizations(term);
  }, [debouncedOrganizationFilter, organizationsTruncated, loadOrganizations]);

  /*
    One list for the popover: everything known, alphabetical, no duplicates. Sorted here rather
    than trusting the merge, because the two sources are each ordered on their own.
  */
  const organizationOptions = useMemo(
    () =>
      [...new Set([...organizationBrowseList, ...organizationMatches])].sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: "base" }),
      ),
    [organizationBrowseList, organizationMatches],
  );

  const selectedContacts = useMemo(
    () => contacts.filter((c) => selected.has(c.id)),
    [contacts, selected]
  );

  const allOnPageSelected =
    contacts.length > 0 && contacts.every((c) => selected.has(c.id));
  const someOnPageSelected = selected.size > 0 && !allOnPageSelected;

  const toggleAll = () =>
    setSelected(allOnPageSelected ? new Set() : new Set(contacts.map((c) => c.id)));

  const toggleOne = useCallback(
    (id: string) =>
      setSelected((current) => {
        const next = new Set(current);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      }),
    []
  );

  /**
   * Flips one contact's favorite flag.
   *
   * Optimistic: the row updates before the request resolves, and reverts to what it was on
   * failure, scoped to this one contact — a coach starring their way down a list should not wait
   * a round trip per click, and a failure on row 9 must not touch the star on row 3. Does not
   * refetch the page; the row it touches is the only thing that changed.
   */
  const handleToggleFavorite = useCallback(async (contact: TrustedContact) => {
    const nextValue = !contact.isFavorite;

    setContacts((current) =>
      current.map((c) => (c.id === contact.id ? { ...c, isFavorite: nextValue } : c))
    );
    setPendingFavoriteIds((current) => {
      const next = new Set(current);
      next.add(contact.id);
      return next;
    });

    try {
      await setTrustedContactFavorite(contact.id, nextValue);
    } catch (err) {
      // Reverts only this row. An API deployed before this endpoint existed 404s/405s here, which
      // getSafeErrorMessage turns into a readable message rather than an unhandled rejection.
      setContacts((current) =>
        current.map((c) => (c.id === contact.id ? { ...c, isFavorite: !nextValue } : c))
      );
      setError(getSafeErrorMessage(err, "Could not update the favorite."));
    } finally {
      setPendingFavoriteIds((current) => {
        const next = new Set(current);
        next.delete(contact.id);
        return next;
      });
    }
  }, []);

  /** Picking a new page size starts the coach back at page 1, for the same reason any other
   *  filter change does — page 2 of a 10-per-page view is not page 2 of a 50-per-page one.
   */
  const handlePageSizeChange = (nextPageSize: number) => {
    setPageSize(nextPageSize);
    savePageSize(nextPageSize);
    setPage(1);
  };

  const handleSave = async (input: TrustedContactInput) => {
    if (editor?.contact) await updateTrustedContact(editor.contact.id, input);
    else await createTrustedContact(input);

    setEditor(null);
    await Promise.all([load(), loadOrganizations("")]);
  };

  const handleDelete = async () => {
    if (!deleting) return;
    setDeleteBusy(true);
    setError(null);
    try {
      await deleteTrustedContact(deleting.id);
      setDeleting(null);
      await Promise.all([load(), loadOrganizations("")]);
    } catch (err) {
      // Close first. ConfirmModal is aria-modal over a full-viewport scrim, so an error written to
      // the page's alert region while it is open sits behind that scrim and outside the screen
      // reader's modal boundary — reported to nobody.
      setDeleting(null);
      setError(getSafeErrorMessage(err, "Could not delete the contact."));
    } finally {
      setDeleteBusy(false);
    }
  };

  /**
   * Removes every selected contact.
   *
   * Soft-deleted server-side, like a single removal — the rows stay so the messaging threads that
   * reference them keep the name they were addressed to. A partial batch comes back as a report
   * rather than an error, so the notice says how many went; see
   * TrustedContactRepository.SoftDeleteManyAsync for why.
   */
  const handleBulkDelete = async () => {
    const ids = (bulkRemoving ?? []).map((c) => c.id);
    if (ids.length === 0) return;

    setBulkBusy(true);
    setError(null);
    try {
      const result = await bulkDeleteTrustedContacts(ids);
      setBulkRemoving(null);
      setSelected(new Set());

      setNotice(
        result.skippedIds.length > 0
          ? `Removed ${result.deletedCount} of ${ids.length}. The rest were already gone.`
          : `Removed ${result.deletedCount} ${result.deletedCount === 1 ? "contact" : "contacts"}.`
      );

      await Promise.all([load(), loadOrganizations("")]);

      /*
        Put focus back in the table. Clearing the selection unmounts the bar holding the button
        that opened the dialog, so ConfirmModal's restore finds a disconnected node and drops
        focus to <body> — leaving a keyboard or screen reader user at the top of the document with
        no position in the rows they were just working in. The notice says what happened; this
        says where they are.
      */
      tableRef.current?.focus();
    } catch (err) {
      // Closed before reporting, for the reason in handleDelete. The selection survives, so a
      // retry is one click.
      setBulkRemoving(null);
      setError(getSafeErrorMessage(err, "Could not remove those contacts."));
    } finally {
      setBulkBusy(false);
    }
  };

  /**
   * Exports the whole filtered directory, not the ten rows on screen — a coach who filters to 87
   * contacts and opens a file holding 10 has been handed the wrong file.
   */
  const handleExport = async () => {
    exportRef.current?.abort();
    const controller = new AbortController();
    exportRef.current = controller;

    setExporting(true);
    setError(null);
    try {
      const { contacts: all, truncated, totalCount: matched } = await getAllTrustedContacts(
        {
          search: debouncedSearch,
          contactType: typeFilter,
          organization: debouncedOrganizationFilter,
          states: stateFilters,
          isFavorite: favoritesOnly || undefined,
        },
        controller.signal
      );
      if (controller.signal.aborted) return;

      downloadBlobFile(
        new Blob([buildTrustedContactsCsv(all)], { type: "text/csv;charset=utf-8;" }),
        trustedContactsCsvFileName()
      );

      // Only written when there is something to say — an unconditional clear would wipe out the
      // "left out, no phone number" notice from a moment ago.
      if (truncated) {
        setNotice(
          `Exported the first ${all.length} of ${matched} contacts. Narrow the filters to export the rest.`
        );
      }
    } catch (err) {
      if (controller.signal.aborted) return;
      setError(getSafeErrorMessage(err, "Could not export the directory."));
    } finally {
      if (!controller.signal.aborted) setExporting(false);
    }
  };

  /**
   * Opens the composer with the selected contacts.
   *
   * Contacts with no dialable number are dropped and named, rather than being carried into a
   * composer that would show them as unsendable — the coach picked ten people and needs to know
   * which eight are actually going to get the text. The message is handed to the composer as well
   * as set here, because the composer covers this page while it is open.
   */
  const handleMessageSelected = () => {
    const sendable = selectedContacts.filter((c) => c.phoneE164);
    const withoutPhone = selectedContacts.filter((c) => !c.phoneE164);

    if (sendable.length === 0) {
      setNotice(
        withoutPhone.length === 1
          ? `${withoutPhone[0].name} has no phone number saved, so there is nobody to text.`
          : "None of the selected contacts have a phone number saved."
      );
      return;
    }

    setNotice(
      withoutPhone.length > 0 ? excludedMessage(withoutPhone.map((c) => c.name)) : null
    );
    setComposeRecipients(sendable.map(toRecipient));
  };

  const messageOne = useCallback((contact: TrustedContact) => {
    if (!contact.phoneE164) {
      setNotice(`${contact.name} has no phone number saved.`);
      return;
    }
    setNotice(null);
    setComposeRecipients([toRecipient(contact)]);
  }, []);

  /**
   * A spreadsheet import landed. Reports every outcome, not just the additions — a coach who
   * uploads 40 rows and sees "12 added" with no explanation assumes it half-failed, and the
   * duplicates are usually why.
   */
  const handleImported = async (result: ImportTrustedContactsResult) => {
    setImportOpen(false);

    const parts: string[] = [
      `${result.importedCount} ${result.importedCount === 1 ? "contact" : "contacts"} added`,
    ];
    if (result.duplicateCount > 0) {
      parts.push(
        `${result.duplicateCount} ${
          result.duplicateCount === 1 ? "was" : "were"
        } already in the directory`
      );
    }
    if (result.skipped.length > 0) {
      parts.push(`${result.skipped.length} had no usable phone number or email`);
    }
    setNotice(`${parts.join(". ")}.`);

    await Promise.all([load(), loadOrganizations("")]);
  };

  const startEdit = useCallback((contact: TrustedContact) => setEditor({ contact }), []);
  const startDelete = useCallback((contact: TrustedContact) => setDeleting(contact), []);

  const searchId = "trusted-contacts-search";
  const typeFilterId = "trusted-contacts-type";
  const organizationFilterId = "trusted-contacts-organization";
  const stateFilterId = "trusted-contacts-state";
  const favoritesFilterId = "trusted-contacts-favorites";

  const excludedNames = composeRecipients
    ? selectedContacts.filter((c) => !c.phoneE164).map((c) => c.name)
    : [];

  return (
    <div className="trusted-contacts">
      {/*
        The subtitle IS the heading, as an <h2>. DashboardHeader already renders "Trusted
        Contacts" as this route's <h1> (getSectionTitle in DashboardScreen), so repeating it here
        showed the title twice — but that <h1> sits outside <main>, so dropping this element
        entirely left the main landmark with no heading at all for anyone navigating by one.
      */}
      <div className="trusted-contacts__header">
        <h2 className="trusted-contacts__subtitle">
          Your network of trusted coaches, scouts, and contacts.
        </h2>
        <Button onClick={() => setEditor({ contact: null })}>+ Add Contact</Button>
      </div>

      <div className="trusted-contacts__toolbar">
        <div className="trusted-contacts__search">
          <label className="trusted-contacts__sr-only" htmlFor={searchId}>
            Search contacts
          </label>
          <TextInput
            id={searchId}
            type="search"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Search contacts"
          />
        </div>

        <div className="trusted-contacts__filter">
          <label className="trusted-contacts__sr-only" htmlFor={typeFilterId}>
            Filter by contact type
          </label>
          <SelectPopover
            id={typeFilterId}
            options={TYPE_OPTIONS}
            value={typeFilter ? contactTypeLabel(typeFilter) : ALL_TYPES}
            onChange={(label) => {
              setTypeFilter(label === ALL_TYPES ? null : contactTypeFromLabel(label));
              setPage(1);
            }}
            placeholder={ALL_TYPES}
            clearable={false}
          />
        </div>

        <div className="trusted-contacts__filter trusted-contacts__filter--stacked">
          <label className="trusted-contacts__sr-only" htmlFor={organizationFilterId}>
            Filter by organization
          </label>
          {/*
            Free text + suggestions, per web/CLAUDE.md — a coach with a large directory cannot
            scan a plain dropdown of every organization on file. The typed text IS the applied
            filter (there is no separate "commit" step), the same as every other ComboboxPopover
            in this app: clearing it back to empty is what restores "all organizations".

            `match="contains"` because these options were already narrowed by the SERVER, whose
            search is a case-insensitive substring match. The default prefix filter would re-filter
            them and throw away most of what was just fetched — typing "state" would fetch
            "Ohio State" and then hide it, leaving an empty menu for a name that plainly exists.
          */}
          <ComboboxPopover
            id={organizationFilterId}
            match="contains"
            suggestWhenEmpty
            options={organizationOptions}
            value={organizationFilter ?? ""}
            onChange={(value) => {
              setOrganizationFilter(value === "" ? null : value);
              setPage(1);
            }}
            placeholder="All organizations"
            fullWidth
          />
          {organizationsTruncated && (
            <p className="trusted-contacts__filter-hint">Keep typing to narrow this.</p>
          )}
        </div>

        <div className="trusted-contacts__filter">
          <label className="trusted-contacts__sr-only" htmlFor={stateFilterId}>
            Filter by state
          </label>
          {/*
            Multi-select, and searchable because fifty states is a long menu to scroll. The API
            takes one `state` parameter per selection, so a single pick sends exactly the request
            it accepted when this was a single-select.
          */}
          <MultiSelectPopover
            id={stateFilterId}
            options={US_STATES}
            value={stateFilters}
            onChange={(next) => {
              setStateFilters(next);
              setPage(1);
            }}
            placeholder={ALL_STATES}
            searchable
          />
        </div>

        {/*
          Switch, not Checkbox: per web/CLAUDE.md's "when does the change land" rule, a checkbox
          implies a form the coach will submit, and this takes effect the moment it flips — there
          is no separate apply step, same as every other filter here.
        */}
        <div className="trusted-contacts__favorites-filter">
          <Switch
            id={favoritesFilterId}
            checked={favoritesOnly}
            onChange={(e) => {
              setFavoritesOnly(e.target.checked);
              setPage(1);
            }}
            label="Favorites only"
          />
        </div>

        <div className="trusted-contacts__toolbar-end">
          <Button variant="secondary" onClick={() => setImportOpen(true)}>
            Import
          </Button>
          <Button variant="secondary" onClick={handleExport} loading={exporting}>
            Export
          </Button>
        </div>
      </div>

      {selected.size > 0 && (
        <div className="trusted-contacts__selection-bar">
          <span>
            {selected.size} {selected.size === 1 ? "contact" : "contacts"} selected
          </span>
          {messagingAvailable && (
            <Button variant="secondary" onClick={handleMessageSelected}>
              Message selected
            </Button>
          )}
          <Button variant="secondary" onClick={() => setSelected(new Set())}>
            Clear
          </Button>
          {/*
            Last, and in the danger colour. Three identical secondary buttons put "removes ten
            people" next to "unticks ten checkboxes" looking exactly alike.
          */}
          <Button
            variant="secondary"
            className="trusted-contacts__danger-button"
            onClick={() => setBulkRemoving(selectedContacts)}
          >
            Remove selected
          </Button>
        </div>
      )}

      {/*
        The live regions stay mounted and their content is toggled inside. A region inserted into
        the DOM with its text already present is unreliably announced, and everything routed
        through `notice` — which contacts were left out of a send, and why — has no other
        presentation. Same shape as messaging/PhoneNumbersPage.
      */}
      <div role="alert" className="trusted-contacts__live-region">
        {error && <div className="trusted-contacts__error">{error}</div>}
      </div>
      <div role="status" aria-live="polite" className="trusted-contacts__live-region">
        {notice && (
          <div className="trusted-contacts__notice">
            {notice}
            <button
              type="button"
              className="trusted-contacts__notice-dismiss"
              onClick={() => setNotice(null)}
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        )}
      </div>

      <div className="data-table-card">
        <div className="data-table-wrap">
          <table
            className="data-table trusted-contacts__table"
            aria-busy={loading}
            aria-label="Trusted contacts"
            ref={tableRef}
            tabIndex={-1}
          >
            <thead>
              <tr>
                <th scope="col" className="trusted-contacts__select-cell">
                  <Checkbox
                    checked={allOnPageSelected}
                    indeterminate={someOnPageSelected}
                    onChange={toggleAll}
                    aria-label="Select all contacts on this page"
                    disabled={contacts.length === 0}
                  />
                </th>
                <th scope="col" className="trusted-contacts__favorite-cell">
                  <span className="trusted-contacts__sr-only">Favorite</span>
                </th>
                <th scope="col">Name</th>
                <th scope="col">Organization / Team</th>
                <th scope="col">State</th>
                <th scope="col">Contact Type</th>
                <th scope="col">Phone</th>
                <th scope="col">Email</th>
                <th scope="col">Notes</th>
                <th scope="col" className="data-table__actions">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {loading && contacts.length === 0 && (
                <tr>
                  <td colSpan={10} className="data-table__empty">
                    Loading contacts…
                  </td>
                </tr>
              )}

              {!loading && contacts.length === 0 && (
                <tr>
                  <td colSpan={10} className="data-table__empty">
                    {totalCount === 0 &&
                    !debouncedSearch &&
                    !typeFilter &&
                    !organizationFilter &&
                    stateFilters.length === 0 &&
                    !favoritesOnly
                      ? "No contacts yet. Add the coaches and scouts your program works with."
                      : "No contacts match those filters."}
                  </td>
                </tr>
              )}

              {contacts.map((contact) => (
                <TrustedContactRow
                  key={contact.id}
                  contact={contact}
                  isSelected={selected.has(contact.id)}
                  canMessage={messagingAvailable}
                  favoritePending={pendingFavoriteIds.has(contact.id)}
                  onToggle={toggleOne}
                  onToggleFavorite={handleToggleFavorite}
                  onMessage={messageOne}
                  onEdit={startEdit}
                  onDelete={startDelete}
                />
              ))}
            </tbody>
          </table>
        </div>

        <TablePagination
          currentPage={page}
          pageSize={pageSize}
          totalCount={totalCount}
          onPageChange={setPage}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          onPageSizeChange={handlePageSizeChange}
        />
      </div>

      <TrustedContactModal
        isOpen={editor !== null}
        contact={editor?.contact ?? null}
        onClose={() => setEditor(null)}
        onSave={handleSave}
      />

      <ConfirmModal
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        title="Remove contact"
        message={
          deleting
            ? `Remove ${deleting.name} from the directory? Everyone on staff loses them.`
            : ""
        }
        confirmText="Remove"
        busy={deleteBusy}
      />

      {/*
        Names them rather than counting them, for the reason `nameList` gives: the answer to "who
        exactly am I about to remove" has to be in the dialog, and the selection is bounded by the
        page size.
      */}
      <ConfirmModal
        isOpen={bulkRemoving !== null}
        onClose={() => setBulkRemoving(null)}
        onConfirm={handleBulkDelete}
        title={`Remove ${bulkRemoving?.length ?? 0} ${
          bulkRemoving?.length === 1 ? "contact" : "contacts"
        }`}
        message={
          bulkRemoving
            ? `Remove ${nameList(bulkRemoving.map((c) => c.name))} from the directory? Everyone on staff loses them.`
            : ""
        }
        confirmText="Remove"
        busy={bulkBusy}
      />

      <ImportContactsModal
        isOpen={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={handleImported}
      />

      {/*
        Mounted only while open. ComposeMessageModal seeds its recipient state in a lazy
        initializer, so a modal left mounted across opens would keep the first selection forever.
      */}
      {composeRecipients && (
        <ComposeMessageModal
          isOpen
          initialRecipients={composeRecipients}
          showRecipientPicker={false}
          notice={excludedNames.length > 0 ? excludedMessage(excludedNames) : undefined}
          onClose={() => setComposeRecipients(null)}
        />
      )}
    </div>
  );
};

/** "Kevin Brown and Matt Taylor were left out — no phone number saved." */
const excludedMessage = (names: string[]): string =>
  `${nameList(names)} ${names.length === 1 ? "was" : "were"} left out — no phone number saved.`;
